import PropTypes from "prop-types";
import React from "react";

import { getBootstrapData } from "@swa-ui/bootstrap";
import { useDeviceInfo } from "@swa-ui/browser";
import { Grid, Link, LoadingDepiction } from "@swa-ui/core";
import {
  CheckboxFormField,
  Form,
  RadioButtonFormField,
  SubmitFormField,
  useForm,
  yupResolver,
} from "@swa-ui/form";
import i18n from "@swa-ui/locale";
import { classNames } from "@swa-ui/string";

import { MfaHeading } from "../MfaHeading";
import { MfaHelpLink } from "../MfaHelpLink";
import styles from "./MfaContactSelectForm.module.scss";
import { getMfaContactSelectFormSchema } from "./MfaContactSelectForm.schema";

/**
 * MfaContactSelectForm allows the user to select contact method they would like to use for MFA.
 */
export const MfaContactSelectForm = (props) => {
  const { contacts, loading, onSubmit } = props;
  const formMethods = useForm({ resolver: yupResolver(getMfaContactSelectFormSchema()) });
  const { MFA_TERMS_AND_CONDITIONS } = getBootstrapData("urls");
  const { screenSize } = useDeviceInfo() ?? {};

  return (
    <Grid {...getGridProps()}>
      <MfaHeading {...getMfaHeadingProps()} />
      <Form
        methods={formMethods}
        insideGrid
        onSubmit={(formData) => {
          onSubmit({ ...formData, contact: JSON.parse(formData.contact) });
        }}
        noValidate
      >
        {!contacts && (
          <div className={classNames(styles.fullWidth)}>
            <LoadingDepiction template={getContactLoadingTemplate()} />
          </div>
        )}
        {contacts && <RadioButtonFormField {...getContactProps()} />}
        <CheckboxFormField {...getTermsAgreeProps()} />
        <MfaHelpLink className={classNames(styles.fullWidth, styles.needHelp)} />
        <SubmitFormField {...getSubmitProps()}>
          {i18n("MfaContactSelectForm__SUBMIT")}
        </SubmitFormField>
      </Form>
    </Grid>
  );

  function getGridProps() {
    return {
      gutterSpacing: 16,
      numberOfColumns: screenSize === "small" ? 4 : 12,
      rowSpacing: 16,
    };
  }

  function getMfaHeadingProps() {
    return {
      disclaimer: hasSMS() && i18n("MfaContactSelectForm__PHONE_DISCLAIMER"),
      key: "contact-select-heading",
      prompt: i18n("MfaContactSelectForm__PROMPT"),
    };
  }

  function getContactLoadingTemplate() {
    return [...Array(2)]
      .map(() => [
        {
          bottomMargin: "xsmall",
          columnDefinitions: [{ gridColumn: "1/-1" }],
          height: "xsmall",
          numberOfColumns: 2,
        },
        {
          bottomMargin: "large",
          columnDefinitions: [{ gridColumn: "1/2" }],
          height: "xsmall",
          numberOfColumns: 2,
        },
      ])
      .flat();
  }

  function getContactProps() {
    return {
      className: classNames(styles.field, styles.contacts),
      componentProps: {
        ariaLiveInput: (name, index, length) =>
          `${i18n("MfaContactSelectForm__RADIO_BUTTON_ARIA_LIVE", {
            index,
            length,
            name,
          })}`,
        defaultValue: JSON.stringify(contacts[0]),
        direction: "column",
        fullWidth: true,
        radioButtonInfoList: contacts.map((contact) => ({
          caption: <div className={styles.contactCaption}>{contact.value}</div>,
          label: (
            <div className={styles.contactLabel}>
              {i18n(`MfaContactSelectForm__${contact.deviceType}_ACTION`)}
            </div>
          ),
          value: JSON.stringify(contact),
        })),
        styleType: "secondary",
      },
      disabled: loading,
      name: "contact",
    };
  }

  function getTermsAgreeProps() {
    return {
      className: classNames(styles.field, styles.termsAgree),
      componentProps: {
        label: (
          <span>
            {i18n("MfaContactSelectForm__TERMS_AGREE")}
            <Link {...getMfaTermsLinkProps()}>
              {i18n("MfaContactSelectForm__TERMS_AND_CONDITIONS")}
            </Link>
          </span>
        ),
      },
      disabled: loading,
      name: "termsAgree",
    };
  }

  function getMfaTermsLinkProps() {
    return {
      "aria-label": i18n("MfaContactSelectForm__TERMS_AND_CONDITIONS_ARIA"),
      external: true,
      href: MFA_TERMS_AND_CONDITIONS,
      newWindow: true,
      onClick: (event) => event.stopPropagation(),
    };
  }

  function getSubmitProps() {
    return {
      className: styles.field,
      componentProps: {
        fullWidth: true,
        submitInProgress: loading,
      },
      name: "submit",
    };
  }

  function hasSMS() {
    return contacts?.some((contact) => contact.deviceType === "SMS");
  }
};

MfaContactSelectForm.propTypes = {
  /** The contacts available for the user to select. */
  contacts: PropTypes.arrayOf(
    PropTypes.shape({
      deviceType: PropTypes.string,
      value: PropTypes.string,
    })
  ),

  /** Indicates if the form is in a loading state. */
  loading: PropTypes.bool,

  /** Callback method to handle form submission. */
  onSubmit: PropTypes.func,
};
